import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Entity, EntityUpdate, EntityTypeLabel } from "@/model";
import { useUpdateEntity } from "@/query/entity";
import { useAgentId, useModal } from "@/hook";
import { Modal } from "@/component/modal";
import { TextInputGroup } from "@/component/input";
import { errorToast, successToast } from "@/component/Toast";
import Button from "@/component/Button";


export const ENTITY_UPDATE_MODAL_ID = "entity-update-modal-id";

const ENTITY_TYPE_OPTIONS: { id: EntityTypeLabel, label: string, describe: string }[] = [
    { id: 'normal', label: 'NORMAL ENTITY', describe: 'i05043' },
    { id: 'post', label: 'POST ENTITY', describe: 'i05044'  },
    { id: 'ignore', label: 'IGNORE ENTITY', describe: 'i05045'  },
];

const EntityModifyModal: React.FC = (props) => {
    const intl = useIntl();
    const agentId = useAgentId();
    const modal = useModal();
    const entity: Entity | undefined = modal.getData(ENTITY_UPDATE_MODAL_ID);

    const [isValidated, setIsValidated] = useState<boolean>(false);
    const [name, setName] = useState<string>("");
    const [entityType, setEntityType] = useState<EntityTypeLabel>();

    const { mutateAsync: updateEntity, isPending: isCreating } = useUpdateEntity(agentId);

    const onClickConfirm = async () => {
        setIsValidated(true);
        if (!entity) {
            return;
        }

        const entityForm: EntityUpdate = {
            agent_id: agentId,
            entity_id: entity?.id,
            name: name.trim(),
            post_processing: entityType === "post",
            ignored: entityType === "ignore",
        };
        if (!entityForm.name) {
            return;
        }

        updateEntity(entityForm)
            .then(() => {
                successToast(intl.formatMessage({ id: "t003" }));
                modal.close(ENTITY_UPDATE_MODAL_ID);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            });
    };

    useEffect(() => {
        if (!entity) {
            return;
        }
        setName(entity.name);
        let defaultType: EntityTypeLabel = "normal";
        if (entity.post_processing) {
            defaultType = "post";
        } else if (entity.ignored) {
            defaultType = "ignore";
        }
        setEntityType(defaultType);
    }, [entity]);

    return (
        <Modal id={ENTITY_UPDATE_MODAL_ID}
               size="md"
               title={intl.formatMessage({ id: "i05012" })}
               onClose={() => setIsValidated(false)}
        >
            <div className="modal-body-8">
                <div className="modal-description flex flex-col gap-6">
                    <TextInputGroup value={name}
                                    invalid={isValidated && !name.trim()}
                                    label={intl.formatMessage({ id: "i05006" })}
                                    placeholder={intl.formatMessage({ id: "i05009" })}
                                    onChange={setName}
                    />

                    <div className="flex flex-col gap-1.5">
                        <div className="input-group-heading">
                            {intl.formatMessage({ id: "i05007" })}
                        </div>
                        <div className="flex items-center gap-4">
                            {ENTITY_TYPE_OPTIONS.map(({ id, label }) => (
                                <label key={id}
                                       className="w-32 flex items-center gap-1 leading-none cursor-pointer"
                                >
                                    <input type="radio"
                                           name="select-entity-type"
                                           className="input-radio-chat42 size-4"
                                           checked={entityType === id}
                                           onChange={() => setEntityType(id)}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="modal-btn-group">
                    <Button size="sm"
                            variant="secondary"
                            disabled={isCreating}
                            onClick={() => modal.close(ENTITY_UPDATE_MODAL_ID)}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isCreating}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default EntityModifyModal;